import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function ucfirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function snakeToTitle(
  input: string | string[] | null | undefined,
): string {
  if (input == null) return "";
  if (Array.isArray(input)) {
    return input
      .filter((v) => v != null && v !== "")
      .map((v) => snakeToTitle(String(v)))
      .join(" · ");
  }
  const str = String(input);
  // Use (^|[\s_]) to find word starts instead of \b\w - \b\w is ASCII-only
  // and treats chars like ø as word boundaries, causing "vektløfting" → "VektløFting"
  return str
    .replace(/_/g, " ")
    .replace(/(^|[\s_])(\S)/g, (_, sep, char) => sep + char.toUpperCase());
}

export function sanitizeUrl(url: string) {
  try {
    return new URL(url).toString();
  } catch {
    return "";
  }
}

export function normalizeUrl(url: string): string {
  if (!url) return "";

  // If URL already has protocol, use sanitizeUrl
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return sanitizeUrl(url);
  }

  // Try adding https:// first
  try {
    return new URL(`https://${url}`).toString();
  } catch {
    // If that fails, try with http://
    try {
      return new URL(`http://${url}`).toString();
    } catch {
      return "";
    }
  }
}

export function tryParseJson<T>(input: unknown): T | null {
  try {
    return JSON.parse(input as string);
  } catch {
    return null;
  }
}

/**
 * Efficiently compares two string arrays for equality (order matters).
 * More performant than JSON.stringify() comparison.
 *
 * @param arr1 - First array
 * @param arr2 - Second array
 * @returns true if arrays are equal in length and content
 */
export function areArraysEqual(arr1: string[], arr2: string[]): boolean {
  if (arr1.length !== arr2.length) return false;
  return arr1.every((item, index) => item === arr2[index]);
}
